
import React, { useState } from 'react';
import { Card, Row, Col, Statistic, Table, Select, Tag, Divider } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer
} from 'recharts';
import useTrainingRequestsOverview from './logic/useTrainingRequestsOverview';
import './TrainingRequestsOverview.scss';

const { Option } = Select;
const COLORS = ['#4069c5', '#82ca9d', '#ff4444'];

const TrainingRequestsOverview = () => {
  const [statusFilter, setStatusFilter] = useState('All');
  const {
    requests,
    stats,
    byDepartment,
    trend,
    loadingRequests,
    loadingStats,
    loadingByDept,
    loadingTrend,
  } = useTrainingRequestsOverview();
  console.log("📊 stats:", stats);
  

  const filteredRequests =
    statusFilter === 'All'
      ? requests
      : requests.filter((req) => req.status.toLowerCase() === statusFilter.toLowerCase());

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };

  const columns = [
    {
      title: 'Requester',
      dataIndex: ['user', 'firstName'],
      key: 'requester',
      render: (text, record) => `${record.user?.firstName || ''} ${record.user?.lastName || ''}`,
    },
    {
      title: 'Training',
      dataIndex: 'courseTitle',
      key: 'courseTitle'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'gray';
        if (status === 'accepted') color = 'green';
        else if (status === 'pending') color = 'gold';
        else if (status === 'rejected') color = 'red';
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type'
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location'
    },
    {
      title: 'Start Date',
      dataIndex: ['trainingDetails', 'trainingStartDate'],
      key: 'trainingStartDate',
      render: (date) =>
        new Date(date).toLocaleDateString('en-GB', {
          year: 'numeric',
          month: 'short',
          day: '2-digit'
        })
    }
  ];

  const pieData = [
    { name: 'Pending', value: parseFloat(stats.pendingRequests || 0) },
    { name: 'Approved', value: parseFloat(stats.approvedRequests || 0) },
    { name: 'Rejected', value: parseFloat(stats.rejectedRequests || 0) }
  ];

  return (
    <div className="training-requests-overview">
      <Divider />

      {/* Header Stats */}
      <Row gutter={16}>
        <Col span={6}>
          <Card>
          <Statistic title="Total Requests" value={stats.totalRequests || 0} />

          </Card>
        </Col>
        <Col span={6}>
          <Card>
          <Statistic title="Pending" value={stats.pendingRequests || 0} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Approved" value={stats.approvedRequests || 0} loading={loadingStats} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Rejected" value={stats.rejectedRequests || 0} loading={loadingStats} />
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Charts */}
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Requests by Department">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={byDepartment}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="pending" fill="#4069c5" name="Pending" />
                <Bar dataKey="approved" fill="#82ca9d" name="Approved" />
                <Bar dataKey="rejected" fill="#ff4444" name="Rejected" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Requests Status Distribution">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Divider />

      <Row gutter={16}>
        <Col span={24}>
          <Card title="Requests Trend Over Time">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="pending" stroke="#4069c5" name="Pending" />
                <Line type="monotone" dataKey="accepted" stroke="#82ca9d" name="Approved" />
                <Line type="monotone" dataKey="rejected" stroke="#ff4444" name="Rejected" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Divider />

      <Row gutter={16}>
        <Col span={24}>
          <Card title="All Training Requests">
            <Table
              columns={columns}
              dataSource={filteredRequests}
              rowKey="requestId"
              loading={loadingRequests}
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TrainingRequestsOverview;
